import i18n from 'i18n';
import uniq from 'lodash-es/uniq';

import type {
  ConversationInsightsData,
  ConversationInsightsTheme,
  ConversationInsightsTopic,
  ExecutiveSummaryData,
  InsightData,
  SessionExplorationData,
  SessionExplorationTopic,
} from '../eventsReport';
import type {
  KnownReportMetricKey,
  RawInsightGroup,
  RawQuote,
  RawReportInsight,
  RawTheme,
  ReportDataBlob,
  ReportStatus,
  ReportingApiReport,
} from './types';
import type { InsightsReportKeyMetric } from 'pages/InsightsReport/types';

/**
 * reporting-api never returns a status (see `ReportingApiReport`), so `completedAt` — set on the
 * first successful data upload — is the only signal. A failed generation is therefore
 * indistinguishable from one still in flight; both read as `processing`.
 */
export const deriveReportStatus = ({ completedAt }: Pick<ReportingApiReport, 'completedAt'>): ReportStatus =>
  completedAt ? 'completed' : 'processing';

/**
 * `active_participants` → `Active Participants`. Underscores, hyphens and camelCase boundaries all
 * become spaces so a key the pipeline invents later still reads as a label rather than as a
 * database column.
 */
export const toMetricTitle = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const MAX_CONVERSATION_RATING = 5;

/**
 * Curated presentation for the keys prod reports carry today: the wording the Hosted Natter report
 * already uses, plus the tooltip that explains what the number counts. Anything not listed still
 * renders — it just gets a derived title and no tooltip, since inventing an explanation for a
 * metric this app has never seen would be worse than omitting one.
 */
const KNOWN_METRICS: Record<KnownReportMetricKey, Omit<InsightsReportKeyMetric, 'key' | 'value'>> = {
  active_participants: {
    title: i18n.t('insightReport:eventReportV2.keyMetrics.participantsTitle'),
    tooltip: i18n.t('insightReport:eventReportV2.keyMetrics.participantsTooltip'),
  },
  natters_facilitated: {
    title: i18n.t('insightReport:eventReportV2.keyMetrics.nattersTitle'),
    tooltip: i18n.t('insightReport:eventReportV2.keyMetrics.nattersTooltip'),
  },
  conversation_rating: {
    title: i18n.t('insightReport:eventReportV2.keyMetrics.conversationRatingTitle'),
    tooltip: i18n.t('insightReport:eventReportV2.keyMetrics.conversationRatingTooltip'),
    maxValue: MAX_CONVERSATION_RATING,
  },
};

const isKnownMetric = (key: string): key is KnownReportMetricKey => key in KNOWN_METRICS;

/**
 * Every entry in `metrics` becomes a card, in the order the pipeline emitted them. Non-finite
 * values are dropped rather than rendered as `NaN`, so a partially-written metrics object degrades
 * to the cards that do have numbers.
 */
export const mapBlobToKeyMetrics = (blob: ReportDataBlob | undefined): InsightsReportKeyMetric[] =>
  Object.entries(blob?.metrics ?? {})
    .filter(([, value]) => typeof value === 'number' && Number.isFinite(value))
    .map(([key, value]) => ({
      key,
      value,
      ...(isKnownMetric(key) ? KNOWN_METRICS[key] : { title: toMetricTitle(key) }),
    }));

const quoteTexts = (quotes: RawQuote[] = []): string[] => quotes.map(({ text }) => text);

const quoteAuthors = (quotes: RawQuote[] = []): number[] => quotes.map(({ user }) => user);

/**
 * The pipeline writes `updatedAt` as epoch seconds with sub-second precision
 * (`"1785522287.594022412"`). Anything non-numeric is passed through untouched so an
 * already-ISO value from a future pipeline version survives rather than becoming `Invalid Date`.
 */
const toIsoTimestamp = (updatedAt: string): string => {
  const seconds = Number(updatedAt);

  if (!updatedAt || Number.isNaN(seconds)) return updatedAt;

  return new Date(Math.round(seconds * 1000)).toISOString();
};

export const mapBlobToExecutiveSummary = (blob: ReportDataBlob | undefined): ExecutiveSummaryData | undefined => {
  const summary = blob?.executiveSummary;

  if (!summary) return undefined;

  return {
    content: summary.content ?? '',
    author: summary.author,
    updatedAt: toIsoTimestamp(summary.updatedAt),
    isPublished: summary.isPublished,
  };
};

const mapThemeToConversationInsightsTheme = (theme: RawTheme): ConversationInsightsTheme => ({
  title: theme.name,
  participantAttribute: theme.participation.percentage.value,
  insights: (theme.insights ?? []).map(({ headline, explanation, keyQuotes, actions }) => ({
    title: headline,
    description: explanation,
    quotes: quoteTexts(keyQuotes),
    suggestionActions: {
      title: actions.summary,
      actions: actions.actions,
      quotes: quoteTexts(actions.keyQuotes),
    },
  })),
});

const mapInsightToConversationInsightsTopic = (insight: RawReportInsight): ConversationInsightsTopic => ({
  title: insight.question.question,
  themes: (insight.themes ?? []).map(mapThemeToConversationInsightsTheme),
});

export const mapBlobToConversationInsights = (blob: ReportDataBlob | undefined): ConversationInsightsData => ({
  topics: (blob?.insights ?? []).map(mapInsightToConversationInsightsTopic),
});

/**
 * Themes, categories and subCategories all collapse to the same `InsightData` view model.
 * `contributingUsers` is not stored per group, so it is derived from the distinct authors of
 * the group's quotes — the same set the UI attributes those quotes to.
 */
const mapGroupToInsightData = (group: RawTheme | RawInsightGroup): InsightData => ({
  title: group.name,
  description: group.description,
  participation: group.participation.percentage.value,
  quotes: quoteTexts(group.keyQuotes),
  userQuotes: (group.keyQuotes ?? []).map(({ text, user }) => ({ quote: text, user })),
  contributingUsers: uniq(quoteAuthors(group.keyQuotes)),
});

/**
 * Every author appearing anywhere under a question — group quotes plus the quotes nested on
 * each theme insight and its suggested actions. Only used for reports that predate the
 * pipeline emitting `contributingUsers` (before ~Oct 2025).
 */
const deriveTopicContributingUsers = (insight: RawReportInsight): number[] => {
  const groupAuthors = [
    ...(insight.themes ?? []),
    ...(insight.categories ?? []),
    ...(insight.subCategories ?? []),
  ].flatMap((group) => quoteAuthors(group.keyQuotes));

  const nestedAuthors = (insight.themes ?? [])
    .flatMap((theme) => theme.insights ?? [])
    .flatMap((themeInsight) => [
      ...quoteAuthors(themeInsight.keyQuotes),
      ...quoteAuthors(themeInsight.actions?.keyQuotes),
    ]);

  return uniq([...groupAuthors, ...nestedAuthors]);
};

const mapInsightToSessionExplorationTopic = (insight: RawReportInsight): SessionExplorationTopic => ({
  title: insight.question.question,
  conversationSummary: insight.summary,
  themes: (insight.themes ?? []).map(mapGroupToInsightData),
  categories: (insight.categories ?? []).map(mapGroupToInsightData),
  subCategories: (insight.subCategories ?? []).map(mapGroupToInsightData),
  contributingUsers: insight.contributingUsers ?? deriveTopicContributingUsers(insight),
  // Anonymity thresholds are community settings, not part of the report payload. The Natalie
  // report has no demographic filters, so nothing consumes this; kept false rather than absent
  // to satisfy the shared view model.
  anonymityFilterEnabled: false,
});

export const mapBlobToSessionExploration = (blob: ReportDataBlob | undefined): SessionExplorationData => ({
  topics: (blob?.insights ?? []).map(mapInsightToSessionExplorationTopic),
});
